import { useEffect, useMemo, useState } from 'react';

import { Modal } from './Modal';
import { Button } from './Button';
import {
  searchBrands,
  searchElectronicModules,
  searchGeneral,
} from '../../services/algolia';
import { getArticles, getCuratedCollections, getParentOrganizations } from '../../services/firebase';
import { filterArticleList } from '../../services/articleSearch';
import { useCalibreSearch } from '../../hooks/useCalibreSearch';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { referenceEntityId } from '../../services/entityLinks';
import {
  ENTITY_LINK_TYPES,
  type EntityLinkType,
} from '../../services/entityLinks';
import {
  PICKABLE_ENTITY_LABELS,
  PICKABLE_ENTITY_PLACEHOLDERS,
  type PickableEntityType,
} from '../../services/pickableEntities';
import type { Article, CuratedCollection, ParentOrganization } from '../../types';

/**
 * Search-and-pick for a single Watchkeeper entity.
 *
 * One modal with a type toggle rather than N toolbar buttons opening N existing
 * pickers: the existing pickers each answer a different question (the calibre
 * picker is built around a brand scope that does not exist inside a prose body,
 * the reference picker is a chip list rather than a single pick), so reusing
 * them would have given one action several different search UIs. Every search
 * here still goes to the backend every other admin surface uses.
 *
 * Two callers, two vocabularies. The rich-text editor passes its four
 * `ENTITY_LINK_TYPES` — the cross-repo wire contract for links embedded in
 * prose. The push and banner composers pass `DEEP_LINK_PICKABLE_TYPES`, which
 * adds manufacturer, module and curated collection: things an admin wants to
 * address by name in a broadcast but nobody links to mid-sentence. See
 * `services/pickableEntities.ts` for why those two sets stay separate.
 *
 * The `types` prop is generic so a caller gets its own narrow union back out of
 * `onSelect` — the rich-text editor still receives an `EntityLinkType` and does
 * not have to re-narrow a value it already constrained on the way in.
 */

/** One row in the results list, normalised across four different backends. */
export interface EntityLinkOption {
  /** The href id path — two segments for a reference, one for the rest. */
  id: string;
  /** The entity's canonical name, stored on the mark as `data-wk-label`. */
  label: string;
  /** Disambiguating detail under the label; never part of the link. */
  sublabel?: string;
}

export interface EntityLinkSelection<
  T extends PickableEntityType = EntityLinkType,
> {
  type: T;
  id: string;
  label: string;
}

const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const RESULT_LIMIT = 15;

/** Algolia reference hit → row. The id carries the brand, which the RN route needs. */
export function referenceToOption(hit: {
  objectID: string;
  brandId: string;
  reference: string;
  model?: string;
  collection?: string;
}): EntityLinkOption {
  const label = [hit.reference, hit.model].filter(Boolean).join(' — ');
  return {
    id: referenceEntityId(hit.brandId, hit.objectID),
    label: label || hit.objectID,
    sublabel: [hit.brandId, hit.collection].filter(Boolean).join(' · '),
  };
}

export function brandToOption(hit: {
  objectID: string;
  name?: string;
  displayName?: string;
  country?: string;
}): EntityLinkOption {
  return {
    id: hit.objectID,
    label: hit.displayName || hit.name || hit.objectID,
    sublabel: [hit.objectID, hit.country].filter(Boolean).join(' · '),
  };
}

export function calibreToOption(entry: {
  id: string;
  name: string;
  brandName?: string;
  brandId?: string;
}): EntityLinkOption {
  return {
    id: entry.id,
    label: entry.name,
    sublabel: entry.brandName || entry.brandId || undefined,
  };
}

/**
 * The article deep link takes the Firestore doc id — `ArticlesService.getArticle`
 * tries that first and only falls back to the legacy `articleId` field — which
 * is also what `CuratedCollection.articleId` already holds.
 */
export function articleToOption(article: Article): EntityLinkOption {
  return {
    id: article.id,
    label: article.title || article.id,
    sublabel: article.enabled ? article.category : 'Disabled',
  };
}

/** Local title/category filter, since articles have no Algolia index. */
export function filterArticles(
  articles: Article[],
  term: string,
): EntityLinkOption[] {
  return filterArticleList(articles, term)
    .slice(0, RESULT_LIMIT)
    .map(articleToOption);
}

export function manufacturerToOption(entry: {
  id: string;
  name: string;
  country?: string;
}): EntityLinkOption {
  return {
    id: entry.id,
    label: entry.name,
    sublabel: [entry.id, entry.country].filter(Boolean).join(' · '),
  };
}

export function moduleToOption(hit: {
  objectID: string;
  name: string;
  manufacturerId?: string;
  displayType?: string;
}): EntityLinkOption {
  return {
    id: hit.objectID,
    label: hit.name,
    sublabel:
      [hit.manufacturerId, hit.displayType].filter(Boolean).join(' · ') ||
      undefined,
  };
}

/**
 * A curated collection, with the two things that decide whether a link to it
 * will do what the admin expects.
 *
 * `targetType` matters because `/landing/{id}` is the route for BOTH shapes: a
 * landing collection renders its authored page, and a search collection is
 * redirected by the client to Advanced Search with the collection's filters.
 * Both are legitimate destinations, so the sublabel reports which one this is
 * rather than the picker refusing half the list.
 *
 * `isActive` is surfaced for the opposite reason — it does NOT gate the deep
 * link. `LandingPageScreen` loads the document by id without consulting it, so
 * a link to a not-yet-active collection still resolves, which is exactly what
 * you want when scheduling a push ahead of a launch. Showing the state stops
 * that being a surprise in either direction.
 */
export function collectionToOption(entry: {
  id: string;
  name: string;
  targetType?: 'search' | 'landing' | 'whatsNew';
  isActive?: boolean;
  testMode?: boolean;
}): EntityLinkOption {
  const shape = entry.targetType === 'landing' ? 'Landing page' : 'Search';
  const flags = [
    shape,
    entry.isActive ? 'Active' : 'Inactive',
    entry.testMode ? 'Test mode' : null,
  ].filter(Boolean);
  return {
    id: entry.id,
    label: entry.name || entry.id,
    sublabel: flags.join(' · '),
  };
}

/** Local name filter, since curated collections have no Algolia index. */
export function filterCollections(
  collections: CuratedCollection[],
  term: string,
): EntityLinkOption[] {
  const q = term.trim().toLowerCase();
  // A What's New card is not addressable as /landing/{id} — that route would
  // open the landing screen with this card's empty body. Its page is /whats-new.
  const linkable = collections.filter((c) => c.targetType !== 'whatsNew');
  const matched = q
    ? linkable.filter((c) => (c.name ?? '').toLowerCase().includes(q))
    : linkable;
  return matched.slice(0, RESULT_LIMIT).map(collectionToOption);
}

/**
 * Local filter over the parent organisations, which load whole (a few dozen
 * docs): display name, full name or alias.
 */
export function filterParentOrgs(
  orgs: Pick<ParentOrganization, 'id' | 'name' | 'displayName' | 'country'>[],
  term: string,
): EntityLinkOption[] {
  const q = term.trim().toLowerCase();
  const matched = q
    ? orgs.filter((o) => [o.displayName, o.name].some((n) => (n ?? '').toLowerCase().includes(q)))
    : orgs;
  return matched.slice(0, RESULT_LIMIT).map((o) => ({
    id: o.id,
    label: o.displayName || o.name,
    sublabel: o.displayName && o.displayName !== o.name ? o.name : o.country,
  }));
}

/** Local name filter over the manufacturer catalogue, which loads whole. */
export function filterManufacturers(
  manufacturers: { id: string; name: string; country?: string }[],
  term: string,
): EntityLinkOption[] {
  const q = term.trim().toLowerCase();
  const matched = q
    ? manufacturers.filter((m) => (m.name ?? '').toLowerCase().includes(q))
    : manufacturers;
  return matched.slice(0, RESULT_LIMIT).map(manufacturerToOption);
}

interface EntityLinkPickerProps<T extends PickableEntityType> {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selection: EntityLinkSelection<T>) => void;
  /**
   * The types this caller offers, in tab order. Defaults to the four
   * rich-text entity types; the deep-link composers pass the wider set.
   */
  types?: readonly T[];
  /** Set when editing an existing link, so the picker opens on its type. */
  initialType?: T;
  /** Modal heading — the composers are not "linking" anything. */
  title?: string;
  /** The words the link will wrap, shown so the curator sees what they are linking. */
  selectedText?: string;
  /**
   * True for a caller that turns the pick into link TEXT — only the rich-text
   * editor does. It controls the "nothing is selected" explanation, which would
   * be nonsense in a composer that is filling in a URL field.
   */
  explainsLinkText?: boolean;
  /**
   * Shown above the results when the active type needs a caveat — the
   * composers use it for the two routes that need a newer app build.
   */
  notice?: string;
  /** Offered only when the caret sits inside an existing entity link. */
  onRemove?: () => void;
}

export function EntityLinkPicker<
  T extends PickableEntityType = EntityLinkType,
>({
  isOpen,
  onClose,
  onSelect,
  types,
  initialType,
  title = 'Link to a Watchkeeper entity',
  selectedText,
  explainsLinkText = false,
  notice,
  onRemove,
}: EntityLinkPickerProps<T>) {
  const tabs = (types ?? (ENTITY_LINK_TYPES as readonly PickableEntityType[])) as readonly T[];
  const defaultType = initialType ?? tabs[0];
  const [type, setType] = useState<T>(defaultType);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [options, setOptions] = useState<EntityLinkOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [parentOrgs, setParentOrgs] = useState<ParentOrganization[] | null>(null);
  const [collections, setCollections] = useState<CuratedCollection[] | null>(
    null,
  );

  useEffect(() => {
    if (isOpen) {
      setType(defaultType);
      setQuery('');
      setDebouncedQuery('');
      setOptions([]);
      setError(null);
    }
  }, [isOpen, defaultType]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  /**
   * Calibres come through the shared hook rather than a bare Algolia call
   * because the index carries neither `aliasDetails` nor `deprecated`; the hook
   * hydrates hits back to real documents. `enabled` keeps it idle on the other
   * three tabs.
   */
  const calibreSearch = useCalibreSearch(query, {
    enabled: isOpen && type === 'calibre',
    hitsPerPage: RESULT_LIMIT,
  });

  /**
   * Manufacturers arrive whole rather than per-keystroke: the catalogue is 176
   * records in a single Algolia request, already shared across nine admin
   * surfaces via this hook, and filtering 176 names in the browser beats a
   * debounced round trip. `enabled` keeps it idle until the tab is open.
   */
  const { manufacturers } = useManufacturerCatalogue({
    enabled: isOpen && type === 'manufacturer',
  });

  useEffect(() => {
    if (!isOpen || type !== 'collection' || collections) return;
    let cancelled = false;
    setSearching(true);
    getCuratedCollections()
      .then((list) => {
        if (!cancelled) setCollections(list);
      })
      .catch((err) => {
        console.error('Failed to load curated collections:', err);
        if (!cancelled) setError('Could not load collections.');
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, type, collections]);

  useEffect(() => {
    if (!isOpen || type !== 'parent-org' || parentOrgs) return;
    let cancelled = false;
    setSearching(true);
    getParentOrganizations()
      .then((list) => {
        if (!cancelled) setParentOrgs(list);
      })
      .catch((err) => {
        console.error('Failed to load parent organisations:', err);
        if (!cancelled) setError('Could not load parent organisations.');
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, type, parentOrgs]);

  useEffect(() => {
    if (!isOpen || type !== 'article' || articles) return;
    let cancelled = false;
    setSearching(true);
    getArticles()
      .then((list) => {
        if (!cancelled) setArticles(list);
      })
      .catch((err) => {
        console.error('Failed to load articles:', err);
        if (!cancelled) setError('Could not load articles.');
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, type, articles]);

  useEffect(() => {
    if (!isOpen) return;
    if (type === 'calibre' || type === 'manufacturer') return;

    if (type === 'article') {
      setOptions(articles ? filterArticles(articles, debouncedQuery) : []);
      return;
    }

    if (type === 'collection') {
      setOptions(
        collections ? filterCollections(collections, debouncedQuery) : [],
      );
      return;
    }

    if (type === 'parent-org') {
      setOptions(parentOrgs ? filterParentOrgs(parentOrgs, debouncedQuery) : []);
      return;
    }

    const q = debouncedQuery.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setSearching(true);
    setError(null);

    const run = async (): Promise<EntityLinkOption[]> => {
      if (type === 'brand') {
        const res = await searchBrands(q, {}, 0, RESULT_LIMIT);
        return res.hits.filter((h) => !h.isDisabled).map(brandToOption);
      }
      if (type === 'module') {
        const hits = await searchElectronicModules(q, {
          hitsPerPage: RESULT_LIMIT,
        });
        return hits.map(moduleToOption);
      }
      const res = await searchGeneral({ query: q, limit: RESULT_LIMIT });
      return res.hits.map(referenceToOption);
    };

    run()
      .then((next) => {
        if (!cancelled) setOptions(next);
      })
      .catch((err) => {
        console.error('Entity search failed:', err);
        if (!cancelled) {
          setOptions([]);
          setError('Search is unavailable right now.');
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, type, debouncedQuery, articles, collections, parentOrgs]);

  const calibreOptions = useMemo(
    () => calibreSearch.results.slice(0, RESULT_LIMIT).map(calibreToOption),
    [calibreSearch.results],
  );

  const manufacturerOptions = useMemo(
    () =>
      type === 'manufacturer'
        ? filterManufacturers(manufacturers, debouncedQuery)
        : [],
    [type, manufacturers, debouncedQuery],
  );

  /**
   * The three tabs whose whole list is already in memory — they filter as you
   * type and show everything on an empty query, so the "type 2 characters"
   * prompt would be a lie and the loading state belongs to the initial fetch
   * rather than to the keystroke.
   */
  const isLocalList =
    type === 'article' || type === 'collection' || type === 'manufacturer' || type === 'parent-org';

  const rows =
    type === 'calibre'
      ? calibreOptions
      : type === 'manufacturer'
        ? manufacturerOptions
        : options;
  const busy = type === 'calibre' ? calibreSearch.loading : searching;
  const needsMoreTyping =
    !isLocalList && type !== 'calibre' && query.trim().length < MIN_QUERY_LENGTH;

  const choose = (option: EntityLinkOption) => {
    onSelect({ type, id: option.id, label: option.label });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <div className="space-y-4">
        {selectedText && (
          <p className="text-sm text-gray-600">
            Linking{' '}
            <span className="font-medium text-gray-900">“{selectedText}”</span>
          </p>
        )}
        {/*
          Only the rich-text editor turns the pick into link text, so only it
          has something to explain here. A composer is filling in a URL field.
        */}
        {!selectedText && explainsLinkText && (
          <p className="text-sm text-gray-600">
            Nothing is selected, so the entity’s name will be inserted as the
            link text.
          </p>
        )}

        {/* A single-type caller has nothing to toggle; the tab row would be
            one permanently-selected button. */}
        <div
          role="tablist"
          aria-label="Entity type"
          className={`rounded-lg border border-gray-300 overflow-hidden ${
            tabs.length > 1 ? 'inline-flex' : 'hidden'
          }`}
        >
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={type === t}
              onClick={() => setType(t)}
              className={`px-3 py-1.5 text-sm border-r last:border-r-0 border-gray-300 transition-colors ${
                type === t
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {PICKABLE_ENTITY_LABELS[t]}
            </button>
          ))}
        </div>

        <input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={PICKABLE_ENTITY_PLACEHOLDERS[type]}
          aria-label={PICKABLE_ENTITY_PLACEHOLDERS[type]}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />

        {notice && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            {notice}
          </p>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="border border-gray-200 rounded-lg divide-y max-h-80 overflow-y-auto">
          {needsMoreTyping && (
            <p className="px-3 py-6 text-sm text-gray-500 text-center">
              Type at least {MIN_QUERY_LENGTH} characters to search.
            </p>
          )}
          {!needsMoreTyping && busy && rows.length === 0 && (
            <p className="px-3 py-6 text-sm text-gray-500 text-center">
              Searching…
            </p>
          )}
          {!needsMoreTyping && !busy && rows.length === 0 && !error && (
            <p className="px-3 py-6 text-sm text-gray-500 text-center">
              No matches.
            </p>
          )}
          {rows.map((option) => (
            <button
              key={`${type}:${option.id}`}
              type="button"
              onClick={() => choose(option)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50"
            >
              <div className="text-sm font-medium text-gray-900 truncate">
                {option.label}
              </div>
              {option.sublabel && (
                <div className="text-xs text-gray-500 truncate">
                  {option.sublabel}
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex justify-between gap-2 pt-2">
          <div>
            {onRemove && (
              <Button variant="secondary" onClick={onRemove}>
                Remove link
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
